import React, { useEffect, useState } from 'react';
import { Sparkles, RefreshCw } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { getProjectAiCost } from '../api/client';

const fmtGbp = (n) => {
  if (n == null) return '£—';
  if (n < 0.01 && n > 0) return `£${n.toFixed(4)}`;
  return `£${n.toFixed(2)}`;
};

/**
 * Small chip that shows running AI-call cost for the current project.
 *
 * Populated by the evidence_ai_calls ledger (`GET /api/evidence/projects/{id}/ai-cost`).
 * Stage 4 will start writing to the ledger when the vision + reasoning layer
 * is wired up. Until then the meter shows £0.00 which is correct: no AI has
 * been called on this project yet.
 */
export const CostMeter = ({ projectId, refreshKey = 0 }) => {
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCost = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await getProjectAiCost(projectId);
      setCost(data);
    } catch (_e) {
      // Non-fatal — the meter is a courtesy widget.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCost(); /* eslint-disable-next-line */ }, [projectId, refreshKey]);

  const total = cost?.total_cost_gbp ?? 0;
  const live = cost?.live_calls ?? 0;
  const cached = cost?.cached_calls ?? 0;
  const savings = cost?.cache_savings_gbp ?? 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="cost-meter"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-900 hover:bg-slate-800 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
        >
          <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-slate-300">AI cost</span>
          <span className="text-emerald-400 font-semibold" data-testid="cost-meter-total">
            {fmtGbp(total)}
          </span>
          <span className="text-slate-500 hidden sm:inline">({live} call{live === 1 ? '' : 's'})</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-slate-900 border-slate-800 text-slate-100 w-72" align="end">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">AI call ledger</div>
          <button
            type="button"
            onClick={fetchCost}
            className="text-slate-400 hover:text-slate-100"
            aria-label="Refresh"
            data-testid="cost-meter-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <dl className="space-y-1.5 text-xs">
          <div className="flex justify-between">
            <dt className="text-slate-400">Total (this project)</dt>
            <dd className="font-mono text-emerald-400" data-testid="cost-meter-total-popover">{fmtGbp(total)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-400">Live calls</dt>
            <dd className="font-mono">{live}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-400">Cached (free)</dt>
            <dd className="font-mono">{cached}</dd>
          </div>
          {savings > 0 && (
            <div className="flex justify-between">
              <dt className="text-slate-400">Saved by cache</dt>
              <dd className="font-mono text-cyan-400">−{fmtGbp(savings)}</dd>
            </div>
          )}
          <div className="flex justify-between pt-2 mt-2 border-t border-slate-800">
            <dt className="text-slate-400">Input tokens</dt>
            <dd className="font-mono">{(cost?.total_input_tokens ?? 0).toLocaleString()}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-400">Output tokens</dt>
            <dd className="font-mono">{(cost?.total_output_tokens ?? 0).toLocaleString()}</dd>
          </div>
          {cost?.total_images ? (
            <div className="flex justify-between">
              <dt className="text-slate-400">Images sent</dt>
              <dd className="font-mono">{cost.total_images.toLocaleString()}</dd>
            </div>
          ) : null}
        </dl>
        {cost?.by_purpose && Object.keys(cost.by_purpose).length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">By purpose</div>
            <ul className="space-y-1 text-xs" data-testid="cost-meter-by-purpose">
              {Object.entries(cost.by_purpose).map(([k, v]) => (
                <li key={k} className="flex justify-between">
                  <span className="text-slate-300">{k.replace(/_/g, ' ')}</span>
                  <span className="font-mono text-slate-400">{v.calls} · {fmtGbp(v.cost_gbp)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="mt-4 text-[10px] text-slate-500 leading-relaxed">
          Cost is estimated from token counts using best-effort provider pricing.
          Exact figures depend on Emergent LLM proxy billing.
        </p>
      </PopoverContent>
    </Popover>
  );
};
